import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  parseCompetitionSchedule,
  extractHomeRoutines,
  type ScheduleEntry,
  type HomeStudioConfig,
} from '@pirouette/shared';
import {
  useStudioProfile,
  useSetStudioProfile,
  useImportCompetitionSchedule,
  useParseCompetitionPdf,
} from '@/data/competitionImport';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/competitions/import')({
  component: ImportCompetitionPage,
});

function fmtDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB — guards the browser from huge text uploads
const MAX_PDF_BYTES = 15 * 1024 * 1024; // 15 MB — must match the Edge Function cap

interface ScanState {
  totalEntries: number;
  distinctActualStudios: number;
  matchedAliases: string[];
  homeRoutines: ScheduleEntry[];
  warnings: number;
}

export function ImportCompetitionPage() {
  const navigate = useNavigate();
  const profile = useStudioProfile();
  const saveProfile = useSetStudioProfile();
  const importSchedule = useImportCompetitionSchedule();
  const parsePdf = useParseCompetitionPdf();

  const [name, setName] = useState('');
  const [aliasesText, setAliasesText] = useState('');
  const [rawText, setRawText] = useState('');
  const [scan, setScan] = useState<ScanState | null>(null);
  const [compName, setCompName] = useState('');
  const [compSeason, setCompSeason] = useState('');
  const [compStart, setCompStart] = useState('');
  const [compEnd, setCompEnd] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  // Seed the studio identity fields once the profile loads.
  useEffect(() => {
    if (profile.data) {
      setName((n) => (n === '' ? profile.data.name : n));
      setAliasesText((a) => (a === '' ? profile.data.aliases.join('\n') : a));
    }
  }, [profile.data]);

  function aliasesArray(): string[] {
    return aliasesText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  async function handleSaveProfile() {
    try {
      await saveProfile.mutateAsync({ name: name.trim(), aliases: aliasesArray() });
      toast({ title: 'Studio identity saved', variant: 'success' });
    } catch (err) {
      toast({
        title: "Couldn't save studio identity",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  function handleFile(file: File) {
    if (file.size > MAX_FILE_BYTES) {
      toast({
        title: 'File too large',
        description: 'Max 5 MB. Use a smaller file or paste the text.',
        variant: 'error',
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setRawText(typeof reader.result === 'string' ? reader.result : '');
    reader.readAsText(file);
  }

  async function handlePdf(file: File) {
    if (file.size > MAX_PDF_BYTES) {
      toast({
        title: 'PDF too large',
        description: 'Max 15 MB. Split the program or export the relevant pages.',
        variant: 'error',
      });
      return;
    }
    try {
      const result = await parsePdf.mutateAsync(file);
      setRawText(result.text);
      setScan(null);
      toast({
        title: `Read ${result.pages} page${result.pages === 1 ? '' : 's'}`,
        description: 'Text extracted — now scan for your routines below.',
        variant: 'success',
      });
    } catch (err) {
      const code = err instanceof Error ? err.message : 'ocr_failed';
      const friendly =
        code === 'mfa_required'
          ? 'Verify admin MFA at Admin → Security, then try again.'
          : code === 'not_configured'
            ? 'PDF OCR isn’t configured yet on the server. Paste the text instead for now.'
            : code === 'too_many_pages'
              ? 'That program has too many pages. Export just the pages you need.'
              : code === 'invalid_pdf'
                ? "That file isn't a readable PDF."
                : 'Could not read the PDF. Paste the text instead, or try a clearer scan.';
      toast({ title: "Couldn't read the PDF", description: friendly, variant: 'error' });
    }
  }

  function handleScan() {
    if (name.trim() === '') {
      toast({
        title: 'Set your studio name first',
        description: 'We match the schedule against your studio name + alias spellings.',
        variant: 'error',
      });
      return;
    }
    const parsed = parseCompetitionSchedule(rawText);
    if (parsed.summary.errors.length > 0) {
      toast({
        title: "Couldn't read the schedule",
        description: parsed.summary.errors[0]?.message ?? 'Check the columns and try again.',
        variant: 'error',
      });
      setScan(null);
      return;
    }
    const home: HomeStudioConfig = { canonical: name.trim(), aliases: aliasesArray() };
    const extracted = extractHomeRoutines(parsed.entries, home);
    setScan({
      totalEntries: extracted.totalEntries,
      distinctActualStudios: extracted.distinctActualStudios,
      matchedAliases: extracted.matchedAliases,
      homeRoutines: extracted.homeRoutines,
      warnings: parsed.summary.warnings.length,
    });
  }

  async function handleImport() {
    if (!scan || scan.homeRoutines.length === 0) return;
    if (compName.trim() === '') {
      toast({ title: 'Name the competition before importing', variant: 'error' });
      return;
    }
    try {
      const result = await importSchedule.mutateAsync({
        competition: {
          name: compName.trim(),
          season: compSeason.trim() || null,
          start_date: compStart || null,
          end_date: compEnd || null,
        },
        routines: scan.homeRoutines.map((r) => ({
          title: r.title,
          category: r.category,
          size: r.size,
          num_dancers: r.numDancers,
          age_division: r.ageDivision,
          level: r.level,
          entry_number: r.entryNumber || null,
          scheduled_day: r.day,
          scheduled_time: r.time,
          duration_seconds: r.durationSeconds,
        })),
      });
      toast({
        title: 'Competition imported',
        description: `${result.routines_created} of your routines added.`,
        variant: 'success',
      });
      await navigate({
        to: '/admin/competitions/$id',
        params: { id: result.competition_id },
      });
    } catch (err) {
      toast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <Link
          to="/admin/competitions"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Competitions
        </Link>
        <h1 className="mt-2">Import a competition schedule</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Paste or upload a competition&rsquo;s master program (CSV or tab/pipe text). We&rsquo;ll
          find your studio in it — across every spelling — and pull out just your routines with all
          their detail.
        </p>
      </header>

      {/* 1. Studio identity */}
      <section aria-labelledby="identity-heading" className="surface-elevated space-y-4 p-5">
        <h2 id="identity-heading" className="font-medium text-[var(--text-lg)]">
          Your studio&rsquo;s name in programs
        </h2>
        <div>
          <Label htmlFor="studio-name">Studio name</Label>
          <Input
            id="studio-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Allegro Dance Academy"
          />
        </div>
        <div>
          <Label htmlFor="studio-aliases">Other spellings it appears under (one per line)</Label>
          <textarea
            id="studio-aliases"
            rows={3}
            value={aliasesText}
            onChange={(e) => setAliasesText(e.target.value)}
            placeholder={'Allegro DA\nAllegro Dance Acad.\nALLEGRO DANCE ACADEMY'}
            className="mt-2 w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-[var(--text-sm)]"
          />
        </div>
        <Button onClick={() => void handleSaveProfile()} disabled={saveProfile.isPending}>
          {saveProfile.isPending ? 'Saving…' : 'Save studio identity'}
        </Button>
      </section>

      {/* 2. The schedule */}
      <section aria-labelledby="schedule-heading" className="surface-elevated space-y-4 p-5">
        <h2 id="schedule-heading" className="font-medium text-[var(--text-lg)]">
          The competition program
        </h2>
        <div>
          <Label htmlFor="schedule-file">Upload a CSV / text program</Label>
          <input
            id="schedule-file"
            type="file"
            accept=".csv,.txt,.tsv,.tab,text/csv,text/plain"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
            }}
            className="mt-2 block w-full text-[var(--text-sm)]"
          />
        </div>
        <div>
          <Label htmlFor="schedule-pdf">…or upload a PDF program (we OCR it for you)</Label>
          <input
            id="schedule-pdf"
            type="file"
            accept="application/pdf,.pdf"
            disabled={parsePdf.isPending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handlePdf(f);
              e.target.value = '';
            }}
            className="mt-2 block w-full text-[var(--text-sm)]"
          />
          <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-xs)]">
            {parsePdf.isPending
              ? 'Reading the PDF… this can take up to a minute for a long program.'
              : 'The PDF is processed only to extract text and is deleted right after — never stored.'}
          </p>
        </div>
        <div>
          <Label htmlFor="schedule-text">…or paste it</Label>
          <textarea
            id="schedule-text"
            rows={8}
            maxLength={MAX_FILE_BYTES}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder={
              'entry,time,title,studio,category,dancers,age,level,duration\n001,08:00,Rise Up,Allegro Dance Academy,Jazz,12,Junior,Competitive,3:10'
            }
            className="mt-2 w-full resize-y rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 font-[var(--font-mono)] text-[var(--text-sm)]"
          />
        </div>
        <Button onClick={handleScan} disabled={!rawText.trim()}>
          Scan for my routines
        </Button>
      </section>

      {/* 3. Preview */}
      {scan && (
        <section aria-labelledby="preview-heading" className="space-y-4">
          <h2 id="preview-heading" className="font-medium text-[var(--text-lg)]">
            Found {scan.homeRoutines.length} of your routines
          </h2>
          <div className="flex flex-wrap gap-2 text-[var(--text-sm)]">
            <Badge tone="neutral">{scan.totalEntries} routines scanned</Badge>
            <Badge tone="neutral">{scan.distinctActualStudios} studios</Badge>
            <Badge tone={scan.homeRoutines.length > 0 ? 'success' : 'warn'}>
              matched {scan.matchedAliases.length} of your spellings
            </Badge>
            {scan.warnings > 0 && <Badge tone="warn">{scan.warnings} rows skipped</Badge>}
          </div>

          {scan.homeRoutines.length === 0 ? (
            <p className="text-[var(--color-ink-muted)]">
              No routines matched your studio. Check the name and spellings above, then scan again.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)]">
              <table className="w-full text-left text-[var(--text-sm)]">
                <thead className="bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)]">
                  <tr>
                    <th className="p-2">#</th>
                    <th className="p-2">Day/Time</th>
                    <th className="p-2">Title</th>
                    <th className="p-2">Category</th>
                    <th className="p-2">Size</th>
                    <th className="p-2">Dancers</th>
                    <th className="p-2">Division</th>
                    <th className="p-2">Level</th>
                    <th className="p-2">Len</th>
                  </tr>
                </thead>
                <tbody>
                  {scan.homeRoutines.map((r, i) => (
                    <tr
                      key={`${r.entryNumber}-${i}`}
                      className="border-t border-[var(--color-border)]"
                    >
                      <td className="p-2">{r.entryNumber || '—'}</td>
                      <td className="p-2">{[r.day, r.time].filter(Boolean).join(' ') || '—'}</td>
                      <td className="p-2 font-medium">{r.title}</td>
                      <td className="p-2">{r.category ?? '—'}</td>
                      <td className="p-2">{r.size ?? '—'}</td>
                      <td className="p-2">{r.numDancers ?? '—'}</td>
                      <td className="p-2">{r.ageDivision ?? '—'}</td>
                      <td className="p-2">{r.level ?? '—'}</td>
                      <td className="p-2">{fmtDuration(r.durationSeconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 4. Competition details + commit */}
          {scan.homeRoutines.length > 0 && (
            <div className="surface-elevated space-y-4 p-5">
              <h3 className="font-medium">Save these as a competition</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="comp-name">Competition name</Label>
                  <Input
                    id="comp-name"
                    value={compName}
                    onChange={(e) => setCompName(e.target.value)}
                    placeholder="Spring Classic 2026"
                  />
                </div>
                <div>
                  <Label htmlFor="comp-season">Season</Label>
                  <Input
                    id="comp-season"
                    value={compSeason}
                    onChange={(e) => setCompSeason(e.target.value)}
                    placeholder="2026"
                  />
                </div>
                <div>
                  <Label htmlFor="comp-start">Start date</Label>
                  <Input
                    id="comp-start"
                    type="date"
                    value={compStart}
                    onChange={(e) => setCompStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="comp-end">End date</Label>
                  <Input
                    id="comp-end"
                    type="date"
                    value={compEnd}
                    onChange={(e) => setCompEnd(e.target.value)}
                  />
                </div>
              </div>
              <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                We import only <strong>your</strong> studio&rsquo;s routines — every other
                studio&rsquo;s entry is discarded and never saved.
              </p>
              <label className="flex items-start gap-2 text-[var(--text-sm)]">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  I have the right to use this competition program and confirm I&rsquo;m importing
                  only my own studio&rsquo;s routines.
                </span>
              </label>
              <Button
                onClick={() => void handleImport()}
                disabled={importSchedule.isPending || compName.trim() === '' || !acknowledged}
              >
                {importSchedule.isPending
                  ? 'Importing…'
                  : `Import ${scan.homeRoutines.length} routines`}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
